import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"
import styled from "styled-components"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetVendorFulfilledOrdersNewQuery, useLazyGetVendorOrdersNewQuery } from "store/api/vendorApi"
import { useAppSelector } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import { TableActionButtonGroup } from "types/table/tableActionButton"
import { ReactComponent as IconDelete } from "assets/svg/icon-delete.svg"
import { ReactComponent as IconDeny } from "assets/svg/icon-deny.svg"
import { ReactComponent as IconEdit } from "assets/svg/icon-edit.svg"
import { ReactComponent as StatusArchivedIcon } from "assets/svg/status-archived.svg"

import { convertVSMOrdersNewToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { DeleteOrderModalNew } from "modals/DeleteOrderModalNew"
import { DenyVendorOrderModal } from "modals/DenyVendorOrderModal"
import { EditVendorOrderModal } from "modals/EditVendorOrderModal"
import { NewVendorOrderModal } from "modals/NewVendorOrderModal"
import { OrderFulfillmentModal } from "modals/OrderFulfillmentModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import { CheckboxNew } from "components/form/CheckboxNew"
import TableWrapper from "components/table/TableWrapper"
import { WidgetVendorOrderStatus } from "components/widgets/WidgetVendorOrderStatus"
import { WidgetWarehouseInformation } from "components/widgets/WidgetWarehouseInformation"

import { getVendorOrdersTableHeaderCells } from "./getVendorOrdersTableHeaderCells"

import styles from "./index.module.scss"

const StyledStatusArchived = styled(StatusArchivedIcon)`
  path {
    stroke: #64748b;
  }

  &:hover {
    path {
      stroke: #334155;
    }
  }
`

export const Orders = () => {
  const [showNewVendorOrderModal, setShowNewVendorOrderModal] = useState(false)
  const [triggerGetOrders, { isFetching, data }] = useLazyGetVendorOrdersNewQuery()
  const [triggerGetFulfilledOrder, { isFetching: isFetchingFulfilled, data: dataFulfilled }] =
    useLazyGetVendorFulfilledOrdersNewQuery()
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const [selectFulfilled, setSelectFulfilled] = useState<boolean>(false)
  const selectedVSMOrder = useAppSelector(state => state.tempData.selectedVSMOrder)
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const [isFirstRender, setFirstRender] = useState(true)
  const meta = selectFulfilled ? dataFulfilled?.meta : data?.meta
  const [updateIndex, setUpdateIndex] = useState(0)
  const [toBeDeletedOrder, setToBeDeletedOrder] = useState<number | null>(null)
  const [toBeDeniedOrder, setToBeDeniedOrder] = useState<number | null>(null)
  const [toBeFulfilledOrder, setToBeFulfilledOrder] = useState<ApiTypes.Model.OrderNew | null>(null)
  const [toBeEditedOrderId, setToBeEditedOrderId] = useState<number | undefined>(undefined)

  const relevantData = selectFulfilled ? dataFulfilled?.data : data?.data

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(getVendorOrdersTableHeaderCells(selectFulfilled), columnsData?.data?.config)
        : getVendorOrdersTableHeaderCells(selectFulfilled),
    [columnsData, selectFulfilled],
  )

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta: meta,
    fileName: "orders",
    convertData: convertVSMOrdersNewToTableRows,
    useLazyQuery: selectFulfilled ? useLazyGetVendorFulfilledOrdersNewQuery : useLazyGetVendorOrdersNewQuery,
  })

  const onEditClick = (id: number) => {
    setToBeEditedOrderId(id)
  }

  const onDeleteClick = (id: number) => {
    setToBeDeletedOrder(id)
  }

  const onDenyClick = (id: number) => {
    setToBeDeniedOrder(id)
  }

  const onNewClick = () => {
    setShowNewVendorOrderModal(true)
  }

  const onShowFulfill = (event: React.FormEvent<HTMLInputElement>) => {
    setSelectFulfilled(event.currentTarget.checked)
    if (event?.currentTarget?.checked) {
      setUrlQueryParams({
        item_status: null,
      })
    } else {
      setUrlQueryParams({
        fulfillment_date_start: null,
        fulfillment_date_end: null,
      })
    }
  }

  const getOrders = async (params: UITypes.Params) => {
    return selectFulfilled ? await triggerGetFulfilledOrder(params) : await triggerGetOrders(params)
  }

  const handleActionSuccess = () => {
    setShowNewVendorOrderModal(false)
    setToBeEditedOrderId(undefined)
    setUpdateIndex(prev => prev + 1)
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await getOrders(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    sendRequest(urlQueryParams)
    triggerGetTableColumns({ list_name: "admin_orders_list" })
  }, [urlQueryParams, selectFulfilled, isFirstRender])

  const onFulfillClickRow = (id: number) => {
    const currentOrder = relevantData?.filter(item => !!item.items.some(item => item.id === id))[0]

    if (!currentOrder) {
      return
    }

    setToBeFulfilledOrder({ ...currentOrder, items: currentOrder.items.filter(item => item.id === id) })
  }

  const getRowActions = (rowData: UITypes.TableBodyRow): TableActionButtonGroup => {
    const item = rowData.rowSettings.item
    const status = rowData.item_status.value
    const id = rowData.rowSettings.id

    const isMainRow = !!rowData.subRows
    const items = Array.isArray(item) ? item : [item]
    const firstItem = items[0]
    const isShippingStatus = firstItem?.status === "shipping"
    const isEditable =
      ["new", "denied", "confirmed"].includes((status as string) || "") &&
      items.every(item => ["new", "denied", "confirmed"].includes(item.status as string))

    return {
      className: classNames(styles.ordersActionsWrapper, {
        [styles.singleItem]: rowData.subRows && rowData.subRows.length === 0,
      }),
      actionButtons: [
        {
          icon: <IconDelete />,
          tooltip: "Delete",
          tooltipPosition: "top" as const,
          hidden: !(isMainRow && status === "new" && items.every(item => item.status === "new")),
          handler: () => onDeleteClick(id),
          className: styles.deleteAction,
        },
        {
          icon: <IconEdit />,
          tooltip: "Edit",
          tooltipPosition: "top" as const,
          hidden: !(isMainRow && isEditable),
          handler: () => onEditClick(id),
          className: styles.editAction,
        },
        {
          icon: <IconDeny />,
          tooltip: "Deny order",
          tooltipPosition: "top" as const,
          hidden: !(
            isMainRow &&
            ["confirmed", "packed"].includes(status as string) &&
            items.every(item => ["confirmed", "packed"].includes(item.status as string))
          ),
          handler: () => onDenyClick(id),
          className: styles.denyAction,
        },
        {
          icon: <StyledStatusArchived />,
          tooltip: "Fulfill",
          tooltipPosition: "top" as const,
          hidden: (isMainRow && items.length !== 1) || !isShippingStatus,
          handler: () => onFulfillClickRow(items[0].id),
        },
      ],
    }
  }

  return (
    <>
      <div className={styles.widgetsWrapper}>
        <WidgetWarehouseInformation updateIndex={updateIndex} />
        <WidgetVendorOrderStatus updateIndex={updateIndex} />
      </div>
      <ContentWrapper title="Orders history">
        <nav className={styles.tableNavWrapper}>
          <div>
            <ButtonNew onClick={onNewClick} color="blue">
              New order
            </ButtonNew>
          </div>
          <div className={styles.tableNavButtonsWrapper}>
            <EditColumnsButton tableName="admin_orders_list" tableHeaderCells={tableHeaderCells} />
            <CheckboxNew onChange={onShowFulfill} className={styles.tableNavItem} isReverse={true} id="fulfilled">
              Show fulfilled
            </CheckboxNew>
            <ButtonNew onClick={handleExport} className={styles.tableNavItem} disabled={meta?.total === 0}>
              Export
            </ButtonNew>
          </div>
        </nav>
        <TableWrapper
          tableName="vendor-orders"
          headerCells={tableHeaderCells}
          columnsLoaded={!!columnsData}
          data={convertVSMOrdersNewToTableRows(relevantData)}
          isLoading={isFetching || isFetchingFulfilled}
          currentItemID={selectedVSMOrder?.id}
          getRowActions={getRowActions}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
          additionalTags={
            selectedVSMOrder
              ? [
                  {
                    label: "Order number",
                    values: [selectedVSMOrder.id],
                  },
                ]
              : []
          }
          rowsExpanded
        />
        <Pagination meta={meta} />
      </ContentWrapper>
      {showNewVendorOrderModal && (
        <NewVendorOrderModal onCancel={() => setShowNewVendorOrderModal(false)} onSuccess={handleActionSuccess} />
      )}
      {toBeDeletedOrder && (
        <DeleteOrderModalNew isVendor orderId={toBeDeletedOrder} onCancel={() => setToBeDeletedOrder(null)} />
      )}
      {toBeDeniedOrder && <DenyVendorOrderModal orderId={toBeDeniedOrder} onCancel={() => setToBeDeniedOrder(null)} />}
      {toBeFulfilledOrder && (
        <OrderFulfillmentModal
          isVendor
          order={toBeFulfilledOrder}
          onCancel={() => setToBeFulfilledOrder(null)}
          onSuccess={() => setToBeFulfilledOrder(null)}
        />
      )}
      {toBeEditedOrderId && (
        <EditVendorOrderModal
          orderId={toBeEditedOrderId}
          onCancel={() => setToBeEditedOrderId(undefined)}
          onSuccess={handleActionSuccess}
        />
      )}
    </>
  )
}
